import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Code2,
  Building2,
  Cpu,
  Eye,
  Hand,
  Infinity,
  LayoutTemplate,
  Link2,
  LineChart,
  Puzzle,
  Gauge,
  Rocket,
  Shield,
  Star,
  Target,
  Users,
  Sparkles,
  GitBranch,
  MessageSquare,
} from "lucide-react";
import FAQSection from "../components/FAQSection";
import BlogSection from "../components/BlogSection";
import { AnimatedCard } from "../components/AnimatedCard";

const CALENDLY = "https://calendly.com/nvseeds/30min";

export const WebDevelopment = () => {
  const webDevelopmentFaqs = [
    {
      question: "Why should we not just use a low-code/no-code platform?",
      answer:
        "Low-code is great for MVPs. But if you want to own your IP, scale without massive licensing fees, and integrate with complex internal databases, you need the control that only custom web development provides.",
    },
    {
      question: "How long does a typical web application development project take?",
      answer:
        "A robust, market-ready web app usually takes 3 to 6 months. With our agile approach, we aim to get a Minimum Viable Product in your hands within 6-8 weeks so you can start gathering real-world data.",
    },
    {
      question: "Do you help with migration from legacy systems?",
      answer:
        "Absolutely. Most of our work involves modernizing existing systems. We specialize in gradually replacing old features with new high-performance web components so there is zero downtime for your business.",
    },
    {
      question: "Is your development team in-house or outsourced?",
      answer:
        "We provide dedicated development teams that act as an extension of your company. We maintain a mix of senior architects and specialized developers to ensure every project has strategic oversight and granular technical execution.",
    },
  ];

  const stats = [
    { label: "Projects delivered", value: "500+", sub: "Across industries" },
    { label: "Global reach", value: "20+", sub: "Countries served" },
    { label: "Client satisfaction", value: "98%", sub: "CSAT-focused delivery" },
    { label: "Speed to market", value: "40%", sub: "Faster vs industry avg." },
  ];

  const phases = [
    {
      icon: Eye,
      phase: "PHASE 1",
      title: "Visibility",
      desc: "The Yellow Pages era — being findable online.",
      outcome: "Low engagement, low impact",
    },
    {
      icon: Hand,
      phase: "PHASE 2",
      title: "Interactivity",
      desc: "Social and SaaS — richer engagement, still siloed.",
      outcome: "Better engagement, limited integration",
    },
    {
      icon: Cpu,
      phase: "PHASE 3",
      title: "Intelligence",
      desc: "Utility and AI — anticipation, speed, full-stack integration.",
      outcome: "High impact, scalable business outcomes",
    },
  ];

  const serviceCards = [
    {
      icon: Code2,
      title: "Custom web development",
      body:
        "Proprietary tools when templates stop scaling. Architecture from thousands to millions of users, security-first patterns, and APIs that talk to CRM, ERP, and SAP ECC.",
    },
    {
      icon: Building2,
      title: "Enterprise web applications",
      body:
        "Multi-user operational systems with strong data visualization and performance-first design for real workflows — not brochureware.",
    },
    {
      icon: LayoutTemplate,
      title: "Website & experience platforms",
      body:
        "UI/UX through deployment: responsive patterns for 2026 usage, Core Web Vitals, and CMS workflows that empower non-technical teams.",
    },
    {
      icon: Gauge,
      title: "Performance & integrations",
      body:
        "CI/CD, automated testing, caching, and observability so your stack stays fast, observable, and ready for AI-adjacent features.",
    },
  ];

  const processSteps = [
    { step: "01", title: "Consultation", desc: "Goals, constraints, and success metrics aligned with your stakeholders." },
    { step: "02", title: "Planning & design", desc: "Architecture, UX flows, and integration map before code hardens." },
    { step: "03", title: "Development", desc: "Agile sprints with weekly visibility — no black-box big reveal." },
    { step: "04", title: "Testing & launch", desc: "Automated checks, accessibility, and staged rollout to production." },
    { step: "05", title: "Post-launch support", desc: "Monitoring, security patches, and iteration as traffic and features grow." },
  ];

  const whyUs = [
    { icon: GitBranch, title: "Agile delivery", text: "Daily stand-ups, weekly demos, and pivot-ready iterations instead of rigid specs." },
    { icon: Shield, title: "Security & quality", text: "Zero-trust patterns, encryption, and testing integrated into development — not bolted on after." },
    { icon: Users, title: "Dedicated expertise", text: "Senior architects and specialists so every build has strategy and execution depth." },
    { icon: Sparkles, title: "Future-ready stack", text: "Modular systems built to plug into next-gen AI and enterprise infrastructure." },
  ];

  return (
    <>
      <Helmet>
        <title>Web Application Development Services | Novaseeds Technologies</title>
        <meta
          name="description"
          content="NV Seeds builds secure, high-performance web applications and platforms — custom, enterprise-grade, and optimized for measurable business outcomes in 2026."
        />
      </Helmet>

      <div className="min-h-screen pt-28 pb-16 md:pb-24 bg-background">
        <section className="relative overflow-hidden border-b border-[#E2E8F0]/80 bg-gradient-to-b from-[#F8FAFC] via-background to-background">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(37,99,235,0.12),transparent)] pointer-events-none" />
          <div className="container relative mx-auto px-4 md:px-6 max-w-7xl pt-10 pb-14 md:pt-14 md:pb-20">
            <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.2em] text-blue-600 mb-4">
              Services · Web
            </p>
            <div className="max-w-4xl">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground tracking-tight leading-[1.08]">
                Web Application{" "}
                <span className="text-gradient">Development</span>
              </h1>
              <p className="mt-5 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
                Building secure, reliable, and high-performance web applications —
                from custom products to enterprise portals — engineered for how your
                business operates in 2026.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(37,99,235,0.35)] transition hover:bg-blue-700"
                >
                  Connect With Us
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={CALENDLY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#CBD5E1] bg-card px-7 py-3.5 text-sm font-semibold text-foreground shadow-sm transition hover:bg-[#F8FAFC]"
                >
                  Book An Appointment
                </a>
              </div>
            </div>

            <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 overflow-visible">
              {stats.map((s, i) => (
                <AnimatedCard
                  key={s.label}
                  index={i}
                  slideFrom="alternate"
                  className="rounded-2xl border border-[#E2E8F0] bg-card/90 backdrop-blur-sm p-4 md:p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                >
                  <p className="text-2xl md:text-3xl font-bold text-foreground tabular-nums">{s.value}</p>
                  <p className="mt-1 text-sm font-semibold text-foreground">{s.label}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{s.sub}</p>
                </AnimatedCard>
              ))}
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl space-y-16 md:space-y-20">
          <section className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start pt-4">
            <div className="lg:col-span-6 space-y-5">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                The digital brochure is dead
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                It is 2026. If your online presence feels like a static relic from 2022,
                you are not just behind the curve — you are invisible to customers. You need
                a high-performance engine that drives revenue, automates workflows, and scales
                without breaking.
              </p>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                At NV Seeds, we don&apos;t just build websites-we engineer{" "}
                <span className="font-semibold text-blue-600">intelligent digital platforms.</span>
              </p>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                We transform complex business logic into high-performance, AI-enabled systems
                that:
              </p>
              <div className="space-y-3">
                {[
                  {
                    icon: LineChart,
                    text: "Capture and convert demand automatically",
                  },
                  {
                    icon: Link2,
                    text: "Integrate with your CRM, ERP, and internal workflows",
                  },
                  {
                    icon: Rocket,
                    text: "Scale without breaking under growth",
                  },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#EEF4FF] text-blue-600 flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5" strokeWidth={1.9} />
                    </div>
                    <p className="text-base md:text-lg leading-relaxed text-muted-foreground">{text}</p>
                  </div>
                ))}
              </div>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                Whether you&apos;re launching a new product, modernizing legacy systems, or
                rebuilding your digital foundation-we deliver systems built for{" "}
                <span className="font-semibold text-blue-600">speed, intelligence, and ROI.</span>
              </p>
            </div>
            <AnimatedCard
              index={0}
              className="lg:col-span-6 rounded-3xl border border-[#E2E8F0] bg-card p-6 md:p-8 shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
            >
              <div className="flex items-center gap-4 pb-5 border-b border-[#E2E8F0]">
                <div className="h-12 w-12 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0">
                  <Star className="h-6 w-6 fill-current" strokeWidth={1.6} />
                </div>
                <h3 className="text-2xl font-semibold text-foreground leading-tight">
                  Why Leading Teams
                  <br />
                  Choose NV Seeds
                </h3>
              </div>

              <div className="mt-5 space-y-0">
                {[
                  {
                    icon: Target,
                    title: "ROI-first delivery",
                    text: "Every sprint is tied to measurable business outcomes-not vanity metrics.",
                  },
                  {
                    icon: Code2,
                    title: "AI-native architecture",
                    text: "Built to integrate AI, automation, and data from day one.",
                  },
                  {
                    icon: Puzzle,
                    title: "Seamless system integration",
                    text: "Works with CRM, ERP, APIs, and legacy systems.",
                  },
                  {
                    icon: Infinity,
                    title: "Continuous delivery model",
                    text: "CI/CD, automated testing, and iterative releases-not risky big launches.",
                  },
                ].map(({ icon: Icon, title, text }, idx) => (
                  <div
                    key={title}
                    className={`flex gap-4 py-5 ${idx !== 3 ? "border-b border-[#E2E8F0]" : ""}`}
                  >
                    <div className="h-12 w-12 rounded-xl bg-[#EEF4FF] text-blue-600 flex items-center justify-center shrink-0">
                      <Icon className="h-6 w-6" strokeWidth={1.8} />
                    </div>
                    <div>
                      <p className="text-xl font-semibold text-foreground">{title}</p>
                      <p className="mt-1.5 text-muted-foreground leading-relaxed">{text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedCard>
          </section>

          <section className="rounded-3xl border border-[#E2E8F0]/80 bg-gradient-to-b from-[#F8FAFC] via-background to-background p-6 md:p-8">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
                The landscape
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                From static to <span className="text-blue-600">intelligent</span>
              </h2>
              <p className="mt-3 text-muted-foreground">
                Three phases — your stack should live in the third.
              </p>
            </div>
            <div className="relative grid md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 md:gap-2 items-center">
              <div
                className="hidden md:block pointer-events-none absolute top-1/2 left-[21%] right-[21%] border-t-2 border-dotted border-blue-200 -translate-y-1/2"
                aria-hidden
              />
              {phases.map(({ icon: Icon, phase, title, desc, outcome }, i) => (
                <div key={title} className="contents">
                  <AnimatedCard
                    index={i}
                    className="rounded-2xl border border-[#E2E8F0] bg-white p-5 md:p-6 transition-colors hover:border-blue-200/80"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="h-10 w-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                        <Icon className="h-5 w-5" strokeWidth={1.9} />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wide text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                        {phase}
                      </span>
                    </div>
                    <p className="mt-4 text-2xl font-bold text-foreground">{title}</p>
                    <div className="mt-2 h-1 w-8 rounded-full bg-blue-600/90" />
                    <p className="mt-4 text-sm text-muted-foreground leading-relaxed min-h-[64px]">{desc}</p>
                    <div className="mt-5 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-700">
                      {outcome}
                    </div>
                  </AnimatedCard>
                  {i < phases.length - 1 && (
                    <div className="hidden md:flex relative z-10 h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white">
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <AnimatedCard
              index={0}
              className="mt-8 rounded-2xl border border-[#E2E8F0] bg-white p-4 md:p-5"
            >
              <div className="flex items-start gap-4">
                <div className="h-14 w-14 rounded-full bg-[#EEF4FF] text-blue-600 flex items-center justify-center shrink-0">
                  <Star className="h-7 w-7" strokeWidth={1.8} />
                </div>
                <div className="text-muted-foreground leading-relaxed">
                  <p>
                    Users expect web applications to anticipate needs, load instantly,
                    and integrate with the full tech stack.
                  </p>
                  <p className="mt-2">
                    If your backend is clunky or your frontend is jittery,{" "}
                    <span className="font-semibold text-blue-600">
                      bounce rate becomes revenue leakage.
                    </span>
                  </p>
                </div>
              </div>
            </AnimatedCard>
          </section>

          <section>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
                Our services
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                Innovative web development solutions
              </h2>
              <p className="mt-3 text-muted-foreground">
                Segmented for CTO and marketing priorities — every build targets measurable ROI.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 overflow-visible">
              {serviceCards.map(({ icon: Icon, title, body }, i) => (
                <AnimatedCard
                  key={title}
                  index={i}
                  slideFrom="alternate"
                  hoverScale={1.04}
                  hoverLift={-10}
                  className="group rounded-3xl border border-[#E2E8F0] bg-card p-8 shadow-[0_2px_8px_rgba(0,0,0,0.05)] hover:shadow-xl hover:border-blue-200/60"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 border border-blue-100 text-blue-600 mb-5 transition-colors group-hover:bg-blue-100/80 group-hover:scale-110 duration-300">
                    <Icon className="h-6 w-6" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">{title}</h3>
                  <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">{body}</p>
                </AnimatedCard>
              ))}
            </div>
          </section>

          <section>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
                How we build
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                World-class delivery, end to end
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 overflow-visible">
              {processSteps.map((s, i) => (
                <AnimatedCard
                  key={s.step}
                  index={i}
                  className="relative rounded-3xl border border-[#E2E8F0] bg-gradient-to-b from-card to-[#F8FAFC]/50 p-6 md:p-7"
                >
                  <span className="text-3xl font-bold text-blue-600">{s.step}</span>
                  <h3 className="mt-2 text-base font-semibold text-foreground">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </AnimatedCard>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-[#E2E8F0] bg-white p-4 md:p-6 lg:p-7 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="text-center">
              <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.2em] text-blue-600">
                The NV Seeds Advantage
              </p>
              <h2 className="mt-2 text-[32px] md:text-5xl font-semibold text-foreground tracking-tight leading-tight">
                Agile Execution. <span className="text-blue-600">AI-Accelerated.</span> Better Outcomes.
              </h2>
              <p className="mt-3 text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                We embed Generative AI across the entire development lifecycle to deliver{" "}
                <span className="font-semibold text-blue-600">faster delivery, higher quality, and lower risk</span>
                —consistently.
              </p>
            </div>

            <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3 items-stretch">
              {[
                { icon: Rocket, label: "Faster Delivery" },
                { icon: Shield, label: "Higher Quality" },
                { icon: Target, label: "Lower Risk" },
                { icon: LineChart, label: "Better ROI" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5 text-sm font-semibold text-foreground flex items-center justify-center gap-2 min-h-[44px]"
                >
                  <Icon className="h-4 w-4 text-blue-600" strokeWidth={1.9} />
                  {label}
                </div>
              ))}
            </div>

            <div className="mt-4 grid lg:grid-cols-12 gap-4 items-start">
              <div className="lg:col-span-9 self-start">
                <div className="rounded-2xl border border-[#E2E8F0] overflow-hidden lg:min-h-[543px]">
                  <div className="grid grid-cols-[1.1fr_1fr_1fr] bg-[#F8FAFC] border-b border-[#E2E8F0] text-sm">
                    <div className="px-4 py-3 font-semibold text-foreground">WHAT MATTERS</div>
                    <div className="px-4 py-3 border-l border-[#E2E8F0]">
                      <p className="font-semibold text-foreground">THE OLD WAY</p>
                      <p className="text-xs text-muted-foreground">Traditional approach</p>
                    </div>
                    <div className="px-4 py-3 border-l border-[#E2E8F0]">
                      <p className="font-semibold text-foreground">THE NV SEEDS WAY</p>
                      <p className="text-xs text-blue-600">AI-accelerated agile</p>
                    </div>
                  </div>
                  {[
                    {
                      icon: MessageSquare,
                      area: "Feedback Loop",
                      note: "How we keep you in the loop",
                      old: "Monthly updates\nLimited visibility",
                      nv: "Daily stand-ups and weekly demos\nReal-time visibility and collaboration",
                    },
                    {
                      icon: Rocket,
                      area: "Deployment",
                      note: "How we ship value",
                      old: "One big launch\nHigh risk, delayed value",
                      nv: "CI/CD with continuous delivery\nSmall, frequent releases. Faster time-to-value.",
                    },
                    {
                      icon: GitBranch,
                      area: "Flexibility",
                      note: "How we adapt to change",
                      old: "Rigid specs from day one\nHard to pivot",
                      nv: "Pivot-ready iterations\nAdapt quickly as priorities evolve.",
                    },
                    {
                      icon: Shield,
                      area: "Testing",
                      note: "How we ensure quality",
                      old: "Post-dev QA\nBugs found late",
                      nv: "Automated testing integrated\ninto development\nQuality built in, not tacked on.",
                    },
                    {
                      icon: LineChart,
                      area: "Decision Intelligence",
                      note: "How we drive smarter choices",
                      old: "Decisions based on intuition\nor limited data",
                      nv: "Data-informed decisions\nthroughout the lifecycle\nClear insights, better prioritization.",
                    },
                  ].map(({ icon: Icon, area, note, old, nv }) => (
                    <div key={area} className="grid grid-cols-[1.1fr_1fr_1fr] border-b last:border-b-0 border-[#E2E8F0] text-sm">
                      <div className="px-4 py-3.5 flex gap-3">
                        <div className="h-9 w-9 rounded-lg bg-[#EEF4FF] text-blue-600 flex items-center justify-center shrink-0">
                          <Icon className="h-4.5 w-4.5" strokeWidth={1.8} />
                        </div>
                        <div>
                          <p className="font-semibold text-foreground">
                            {area} {area === "Decision Intelligence" && <span className="text-[10px] ml-1 font-bold text-blue-600">NEW</span>}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">{note}</p>
                        </div>
                      </div>
                      <div className="px-4 py-3.5 border-l border-[#E2E8F0] whitespace-pre-line text-muted-foreground leading-relaxed">
                        {old}
                      </div>
                      <div className="px-4 py-3.5 border-l border-[#E2E8F0] whitespace-pre-line text-emerald-700 leading-relaxed font-medium">
                        {nv}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-2 md:mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-stretch">
                  {[
                    {
                      icon: Gauge,
                      value: "30-50%",
                      label: "Faster Development",
                      sub: "AI-assisted coding and automation reduce development time.",
                    },
                    {
                      icon: Shield,
                      value: "40-70%",
                      label: "Fewer Production Defects",
                      sub: "AI-driven reviews and testing catch issues earlier.",
                    },
                    {
                      icon: LineChart,
                      value: "2-3x",
                      label: "Higher Release Velocity",
                      sub: "Smarter prioritization and automation improve team throughput.",
                    },
                    {
                      icon: Sparkles,
                      value: "The Result",
                      label: "",
                      sub: "Faster delivery. Higher quality. Lower risk. Every single release.",
                      isResult: true,
                    },
                  ].map(({ icon: Icon, value, label, sub, isResult }) => (
                    <div
                      key={value + sub}
                      className={`rounded-xl border p-3.5 min-h-[180px] h-full flex flex-col ${
                        isResult
                          ? "border-[#D8E5FF] bg-[#F5F9FF]"
                          : "border-[#E2E8F0] bg-[#F8FAFC]"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="h-4.5 w-4.5 text-blue-600 shrink-0" strokeWidth={1.9} />
                        <p
                          className={`whitespace-pre-line leading-tight ${
                            isResult ? "text-lg font-semibold text-blue-700" : "text-2xl font-bold text-blue-600"
                          }`}
                        >
                          {value}
                        </p>
                      </div>
                      {label && <p className="text-sm font-semibold text-foreground mt-0.5">{label}</p>}
                      <p className={`${isResult ? "text-xs text-blue-800" : "text-xs text-muted-foreground"} mt-1 leading-relaxed`}>
                        {sub}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-3 space-y-5 lg:max-w-[280px] xl:max-w-[300px] lg:justify-self-end">
                {[
                  { icon: Code2, title: "AI-Powered Code Generation", text: "We use Gen AI to write clean, efficient code faster-following best practices and proven patterns." },
                  { icon: Star, title: "AI Code Review", text: "Intelligent analysis for quality, security, performance, and edge cases-reducing human error." },
                  { icon: CheckCircle2, title: "AI-Generated Test Cases", text: "We create comprehensive, edge-case test scenarios automatically-improving coverage and reliability." },
                  { icon: Cpu, title: "Automation Everywhere", text: "AI helps us automate repetitive tasks, documentation, and workflows so teams focus on what matters most." },
                ].map(({ icon: Icon, title, text }) => (
                  <div key={title} className="rounded-xl border border-[#E2E8F0] bg-white px-4 py-5 md:py-6 min-h-[148px] flex flex-col justify-center">
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-lg bg-[#EEF4FF] text-blue-600 flex items-center justify-center shrink-0">
                        <Icon className="h-5 w-5" strokeWidth={1.8} />
                      </div>
                      <div>
                        <p className="font-semibold text-blue-700">{title}</p>
                        <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{text}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

            </div>

            <div className="mt-3 rounded-xl border border-[#D6E4FF] bg-gradient-to-r from-[#F5F9FF] to-white p-3 md:p-3.5 grid lg:grid-cols-3 items-center gap-3">
              <div className="flex items-center gap-2.5 text-center lg:text-left justify-center lg:justify-start">
                <div className="h-8 w-8 rounded-full bg-[#EEF4FF] text-blue-600 flex items-center justify-center shrink-0">
                  <Target className="h-4.5 w-4.5" strokeWidth={1.9} />
                </div>
                <p className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">We don&apos;t just follow agile.</span>{" "}
                  We accelerate it with Generative AI to deliver{" "}
                  <span className="font-semibold text-blue-600">outcomes that matter—faster.</span>
                </p>
              </div>
              <p className="text-sm text-center text-foreground">See how we can accelerate your next project.</p>
              <div className="flex justify-center lg:justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(37,99,235,0.35)] hover:bg-blue-700"
                >
                  Talk to Our Experts
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-[#E2E8F0] bg-[#F8FAFC] p-8 md:p-12">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                Why work with NV Seeds
              </h2>
              <p className="mt-3 text-muted-foreground">
                Technical debt is a survival-level risk in 2026 — we ship modular, documented systems.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 overflow-visible">
              {whyUs.map(({ icon: Icon, title, text }, i) => (
                <AnimatedCard
                  key={title}
                  index={i}
                  className="rounded-2xl bg-card border border-[#E2E8F0] p-6 shadow-sm"
                >
                  <Icon className="h-8 w-8 text-blue-600 mb-3" strokeWidth={1.5} />
                  <h3 className="font-semibold text-foreground">{title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{text}</p>
                </AnimatedCard>
              ))}
            </div>
            <AnimatedCard
              index={4}
              hoverScale={1.02}
              hoverLift={-4}
              className="mt-10 rounded-2xl border border-blue-200 bg-blue-50/50 p-6 md:p-8"
            >
              <p className="font-medium text-foreground mb-2">Technical debt, addressed</p>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                A cheap vendor can ship something that works today and fails tomorrow. NV Seeds
                builds future-ready systems designed for integration with next-generation AI and
                enterprise infrastructure — so your web stack stays an asset, not an anchor.
              </p>
            </AnimatedCard>
          </section>

          <AnimatedCard
            index={0}
            hoverScale={1}
            hoverLift={-8}
            className="grid lg:grid-cols-12 gap-8 items-stretch rounded-3xl border border-[#E2E8F0] bg-card overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
          >
            <div className="lg:col-span-7 p-8 md:p-10 lg:p-12 border-b lg:border-b-0 lg:border-r border-[#E2E8F0]">
              <p className="text-sm font-semibold text-blue-600 mb-2">Case study</p>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-6">
                Modernizing a global logistics portal
              </h2>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">The client:</span> A Tier-1 logistics
                provider on a legacy PHP framework.
              </p>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">The problem:</span> High latency drove
                a 22% drop in booking completions; no real-time tracking.
              </p>
              <p className="text-muted-foreground mb-4">
                <span className="font-medium text-foreground">The solution:</span> Full custom web
                overhaul — reactive frontend, microservices backend.
              </p>
              <p className="font-medium text-foreground mb-2">The ROI</p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  45% improvement in load speeds
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  18% increase in booking conversions within one quarter
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  30% reduction in server costs via optimized cloud allocation
                </li>
              </ul>
            </div>
            <div className="lg:col-span-5 flex flex-col bg-gradient-to-br from-blue-600 to-blue-800 p-8 md:p-10 text-white min-h-full">
              <MessageSquare className="h-10 w-10 opacity-90 mb-4" />
              <p className="text-lg font-semibold leading-snug">Your 2026 playbook for web success</p>
              <ul className="mt-6 space-y-3 text-sm text-blue-100 leading-relaxed">
                <li>· Audit speed — above ~1.5s interactivity loses revenue</li>
                <li>· Connect platforms; avoid isolated islands</li>
                <li>· Modern encryption and compliance by design</li>
                <li>· Ship user outcomes, not vanity features</li>
              </ul>
            </div>
          </AnimatedCard>

          <AnimatedCard
            index={0}
            hoverScale={1.02}
            hoverLift={-6}
            className="rounded-3xl border border-blue-200 bg-gradient-to-r from-blue-50 to-[#EFF6FF] px-6 py-10 md:px-12 md:py-14 text-center"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground">
              Ready to build your next web application?
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              Book a session with our team or send a brief — we will help you prioritize what ships first.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(37,99,235,0.35)] hover:bg-blue-700"
              >
                Book An Appointment
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full border border-[#CBD5E1] bg-white px-8 py-3.5 text-sm font-semibold text-foreground hover:bg-[#F8FAFC]"
              >
                Contact us
              </Link>
            </div>
          </AnimatedCard>

          <BlogSection
            categorySlug="software-development-company"
            title="What's New In The Market"
            maxPosts={3}
          />
          <FAQSection faqs={webDevelopmentFaqs} title="Frequently Asked Questions" />
        </div>
      </div>
    </>
  );
};
